import { useEffect, useState, useMemo } from 'react';
import * as helpers from "../utils/utils.js"
import { useClientId } from "../context/context.js";

export function usePricing({ selectedSlots }) {
    const [pricingData, setPricingData] = useState([]);
    const [discounts, setDiscounts] = useState({}); // all discounts array
    const [manualEnteredDiscounts, setManualEnteredDiscounts] = useState(new Set()); // manual discountID array
    const [autoDiscounts, setAutoDiscounts] = useState([]); // auto discountID array
    const [appliedDiscount, setAppliedDiscount] = useState({amount: 0, code: ""}); // applied discount array

    const clientId = useClientId();

    useEffect(() => {
        // console.log("applied discount:", appliedDiscount)
        // console.log("manual Discounts", manualEnteredDiscounts)
    }, [appliedDiscount, manualEnteredDiscounts])

    useEffect(() => {
        const fetchPricing = async () => {
            const response = await helpers.get(`/clients/${clientId}/prices`); // Or any API call

            if (response.success) {  
                const priceMap = {};
                for (let price of response.data) {
                    priceMap[price.id] = price;
                }
                setPricingData(priceMap);
            }
        };

        const fetchDiscounts = async () => {
            const response = await helpers.get(`/clients/${clientId}/discounts`);
    
            if (!response.success) {
                return 
            }
            const data = response.data;
    
            const discountMap = {};
            const autoDiscountArr = [];
            for (let discount of data) {
                discountMap[discount.id] = discount;
        
                // check if discount is manual or automatic
                if (discount.type === "automatic") {
                    autoDiscountArr.push(discount.id);
                }
            }
    
            setDiscounts(discountMap);
            setAutoDiscounts(autoDiscountArr);
        };
    
        fetchDiscounts();
        fetchPricing();
    }, [clientId]);  // This effect runs once when the component is mounted.


    useEffect(() => {

        let bestDiscount = {amount: 0, code: ""};

        // check if an automatic discount is applied
        for (let discountId of autoDiscounts) {
            const discountedAmount = validateDiscount(selectedSlots, discountId, pricingData, discounts);

            if (discountedAmount > bestDiscount.amount) {
                bestDiscount = {
                    code: discountId,
                    amount: discountedAmount,
                };
            }
        }

        // check if a manual discount is applied
        for (let discountId of manualEnteredDiscounts) {
            const discountedAmount = validateDiscount(selectedSlots, discountId, pricingData, discounts);
        
            if (discountedAmount > bestDiscount.amount) {
                bestDiscount = {
                    code: discountId,
                    amount: discountedAmount,
                };
            }
        }

        // apply the best discount
        setAppliedDiscount(bestDiscount);

    }, [selectedSlots, discounts, autoDiscounts, manualEnteredDiscounts, pricingData])

    const totals = useMemo(() => {

        let total = 0;
        let deposit = 0;

        if (appliedDiscount === null || Object.keys(pricingData).length === 0) {
            return {total, deposit};
        }

        let depositSubtotal = 0;

        for (let slot of selectedSlots) {
            const price = pricingData[slot.priceId];
            total += price?.price || 0;
            depositSubtotal += price?.deposit || 0;
        }

        // apply discount
        if (appliedDiscount.amount > 0) {
            total -= appliedDiscount.amount;
        }

        total = Math.max(0, helpers.round2(total));
        deposit = helpers.round2(Math.min(depositSubtotal, total));

        return {total, deposit};
    }, [appliedDiscount, selectedSlots, pricingData]);


    const getPrice = (priceId) => {
        return pricingData[priceId] || null;
    }


    // validates a discount and returns amount of money that is saved because of it (0 if not valid)
    const validateDiscount = (selectedSlots, discountId, priceMap, discounts) => {
        if (!discounts[discountId]) {
            return 0;
        }

        const criteria = discounts[discountId].criteria;
        let isValid = false;

        if (criteria.type === "slot") {
            const numSlotsBooked = selectedSlots.length;
            isValid = numSlotsBooked >= criteria.min;
        } else {
            const numBooked = selectedSlots.filter(
            (slot) => priceMap[slot.priceId]?.type === criteria.type,
            ).length;
            isValid = numBooked >= criteria.min;
        }

        if (!isValid) {
            return 0;
        }

        // calculate the current price based on selected slots
        const currentPrice = selectedSlots.reduce((acc, slot) => {
            const price = priceMap[slot.priceId]?.price;
            return acc + price;
        }
        , 0);

        const effect = discounts[discountId].effect;

        // percentage discount
        if (effect.type === "percent") {
            return currentPrice * effect.amount;
        }

        // dollar amount discount
        if (effect.type === "money") {
            return effect.amount;
        }

        // cheapest day free discount
        if (effect.type === "day") {
            const sortedSlots = [...selectedSlots].sort((a,b) => priceMap[a.priceId].price - priceMap[b.priceId].price)
            let discountedAmount = 0
            for (let i=0; i<effect.amount; i++) {
            discountedAmount = discountedAmount + priceMap[sortedSlots[i].priceId].price;
            }
            return discountedAmount;
        }

        return 0;
    }

    // check if a manual discount is valid and apply it
    const addManualDiscount = (discountId) => {
        let discountApplied = false;
        let message = "";

        if (!discountId) {
            message = "Enter a discount code";
        } else if (discounts[discountId]) {
            
            setManualEnteredDiscounts((prevDiscounts) => {
                prevDiscounts.add(discountId);
                return new Set(prevDiscounts);
            });

        } else {
            message = "The code you entered is not valid";
        }

        return {applied: discountApplied, message: message};
    }
    


      
    return {
        getPrice,
        pricingData,
        discounts,
        appliedDiscount,
        manualEnteredDiscounts,
        addManualDiscount,
        totals
    };
}
